import {Dispatch, PropsWithChildren, SetStateAction, useState} from "react";
import {Button, Result} from "antd";
import {GetCustomerUpcomingCallsApiResponse} from "../../../react-query-hooks/session/upcoming-calls/types";
import {createContext} from "../../create-context";
import {FullPageLoader} from "../../../components/full-page-loader";
import {useGetCustomerUpcomingCalls} from "../../../react-query-hooks/session/upcoming-calls";
import {PaginationMeta} from "../../../redux/service/i-service";

type GetCustomerUpcomingCallsState = {
  customerUpcomingCallsData: GetCustomerUpcomingCallsApiResponse;
  customerUpcomingCallsApiMetaResponse: PaginationMeta;
  isFetchingCustomerUpcomingCalls: boolean;
  isPreviousCustomerUpcomingCallsData: boolean;
  isLoadingCustomerUpcomingCalls: boolean;
  refetchCustomerUpcomingCalls: () => void;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
};

type GetCustomerUpcomingCallsContextState = {
  state: GetCustomerUpcomingCallsState;
};

const [useContext, ContextProvider] =
  createContext<GetCustomerUpcomingCallsContextState>();

export const useGetCustomerUpcomingCallsContext = useContext;

export const GetCustomerUpcomingCallsProvider = ({children}: PropsWithChildren) => {
  const [page, setPage] = useState(1);
  const getCustomerUpcomingCallsQuery = useGetCustomerUpcomingCalls(page);

  if (getCustomerUpcomingCallsQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (
    getCustomerUpcomingCallsQuery.isError &&
    getCustomerUpcomingCallsQuery.error.message !==
      "Sorry, You currently do not have any upcoming calls! Please, book a session and try again"
  ) {
    return (
      <Result
        status="500"
        title="Sorry, something went wrong."
        subTitle="Error fetching upcoming calls"
        extra={
          <Button
            type="primary"
            size="small"
            onClick={() => getCustomerUpcomingCallsQuery.refetch()}
          >
            Try again
          </Button>
        }
      />
    );
  }

  function refetchCustomerUpcomingCalls() {
    getCustomerUpcomingCallsQuery.refetch();
  }

  const state: GetCustomerUpcomingCallsState = {
    customerUpcomingCallsData: getCustomerUpcomingCallsQuery.isSuccess
      ? getCustomerUpcomingCallsQuery.data.data
      : {
          session: [],
        },
    customerUpcomingCallsApiMetaResponse: getCustomerUpcomingCallsQuery.isSuccess
      ? getCustomerUpcomingCallsQuery.data.meta
      : {
          current_page: 1,
          next_page: 0,
          per_page: 5,
          previous_page: 0,
          total_pages: 0,
        },
    isFetchingCustomerUpcomingCalls: getCustomerUpcomingCallsQuery.isFetching,
    isLoadingCustomerUpcomingCalls: getCustomerUpcomingCallsQuery.isLoading,
    isPreviousCustomerUpcomingCallsData: getCustomerUpcomingCallsQuery.isPreviousData,
    refetchCustomerUpcomingCalls,
    page,
    setPage,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
